/**
 * After Hours Bundle - Shopify JavaScript
 * Copy this file to your theme's assets/ folder
 * Include in theme.liquid before </body>: <script src="{{ 'after-hours.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // ============================================
  // AUDIO PLAYER
  // ============================================
  
  class AudioPlayer {
    constructor() {
      this.currentlyPlaying = null;
      this.audioElements = {};
      this.progressIntervals = {};
      this.init();
    }

    init() {
      document.addEventListener('click', (e) => {
        const playBtn = e.target.closest('[data-audio-play]');
        if (playBtn) {
          this.handlePlayClick(playBtn);
        }

        const waveform = e.target.closest('[data-audio-waveform]');
        if (waveform) {
          this.handleWaveformClick(e, waveform);
        }

        // Scrubber bar click handler (for master preview)
        const scrubber = e.target.closest('[data-audio-scrubber]');
        if (scrubber) {
          this.handleScrubberClick(e, scrubber);
        }
      });
    }

    handlePlayClick(btn) {
      const audioId = btn.dataset.audioPlay;
      const audioUrl = btn.dataset.audioUrl;

      if (!audioUrl) return;

      if (this.currentlyPlaying === audioId) {
        this.pause(audioId);
      } else {
        if (this.currentlyPlaying) {
          this.pause(this.currentlyPlaying);
        }
        this.play(audioId, audioUrl);
      }
    }

    play(audioId, audioUrl) {
      let audio = this.audioElements[audioId];

      if (!audio) {
        audio = new Audio(audioUrl);
        this.audioElements[audioId] = audio;

        audio.addEventListener('loadedmetadata', () => {
          this.updateDuration(audioId, audio.duration);
        });

        audio.addEventListener('ended', () => {
          this.onEnded(audioId);
        });
      }

      audio.play();
      this.currentlyPlaying = audioId;
      this.setPlayingState(audioId, true);
      this.startProgressUpdate(audioId, audio);
    }

    pause(audioId) {
      const audio = this.audioElements[audioId];
      if (audio) {
        audio.pause();
      }
      this.currentlyPlaying = null;
      this.setPlayingState(audioId, false);
      this.stopProgressUpdate(audioId);
    }

    onEnded(audioId) {
      this.currentlyPlaying = null;
      this.setPlayingState(audioId, false);
      this.stopProgressUpdate(audioId);
      this.updateProgress(audioId, 0);
    }

    setPlayingState(audioId, isPlaying) {
      const playerContainer = document.querySelector(`[data-audio-player="${audioId}"]`);
      const playBtn = document.querySelector(`[data-audio-play="${audioId}"]`);
      
      if (playerContainer) {
        playerContainer.classList.toggle('is-playing', isPlaying);
      }
      
      if (playBtn) {
        playBtn.classList.toggle('is-playing', isPlaying);
        const playIcon = playBtn.querySelector('.play-icon');
        const pauseIcon = playBtn.querySelector('.pause-icon');
        if (playIcon) playIcon.style.display = isPlaying ? 'none' : 'block';
        if (pauseIcon) pauseIcon.style.display = isPlaying ? 'block' : 'none';
      }
    }

    startProgressUpdate(audioId, audio) {
      this.stopProgressUpdate(audioId);
      this.progressIntervals[audioId] = setInterval(() => {
        if (audio.duration) {
          const progress = audio.currentTime / audio.duration;
          this.updateProgress(audioId, progress);
        }
      }, 50);
    }

    stopProgressUpdate(audioId) {
      if (this.progressIntervals[audioId]) {
        clearInterval(this.progressIntervals[audioId]);
        delete this.progressIntervals[audioId];
      }
    }

    updateProgress(audioId, progress) {
      // Waveform bars (for pack preview players)
      const waveform = document.querySelector(`[data-audio-waveform="${audioId}"]`);
      if (waveform) {
        const bars = waveform.querySelectorAll('.ah-waveform-bar');
        bars.forEach((bar, i) => {
          const barProgress = i / bars.length;
          const isActive = barProgress < progress;
          bar.classList.toggle('is-active', isActive);

          // Match Lovable style: muted champagne inactive, brighter active
          bar.style.setProperty(
            'background-color',
            isActive ? 'rgba(201, 174, 127, 0.9)' : 'rgba(201, 174, 127, 0.35)',
            'important'
          );
        });
      }

      // Scrubber bar (for master preview player)
      const scrubber = document.querySelector(`[data-audio-scrubber="${audioId}"]`);
      if (scrubber) {
        const fill = scrubber.querySelector('.ah-scrubber-fill');
        if (fill) {
          // Use setProperty with !important to override init styles
          fill.style.setProperty('width', `${progress * 100}%`, 'important');
        }
      }

      // Update duration display with current time for scrubber-style players
      const audio = this.audioElements[audioId];
      if (audio && audio.duration) {
        const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
        if (durationEl && durationEl.textContent.includes('/')) {
          // Format as "current / total" for scrubber-style players
          durationEl.textContent = `${this.formatTime(audio.currentTime)} / ${this.formatTime(audio.duration)}`;
        }
      }
    }

    updateDuration(audioId, duration) {
      const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
      if (durationEl) {
        // Check if it's a scrubber-style player (has "/" in default text)
        if (durationEl.textContent.includes('/')) {
          durationEl.textContent = `0:00 / ${this.formatTime(duration)}`;
        } else {
          durationEl.textContent = this.formatTime(duration);
        }
      }
    }

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    handleWaveformClick(e, waveform) {
      const audioId = waveform.dataset.audioWaveform;
      const audio = this.audioElements[audioId];
      
      if (!audio || !audio.duration) return;

      const rect = waveform.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const progress = Math.max(0, Math.min(1, clickX / rect.width));
      
      audio.currentTime = progress * audio.duration;
      this.updateProgress(audioId, progress);
    }

    handleScrubberClick(e, scrubber) {
      const audioId = scrubber.dataset.audioScrubber;
      const audio = this.audioElements[audioId];
      
      if (!audio || !audio.duration) return;

      const rect = scrubber.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const progress = Math.max(0, Math.min(1, clickX / rect.width));
      
      audio.currentTime = progress * audio.duration;
      this.updateProgress(audioId, progress);
    }
  }

  // ============================================
  // STICKY BAR
  // ============================================

  class StickyBar {
    constructor() {
      this.stickyBar = document.querySelector('.ah-sticky-bar');
      this.triggerElement = document.getElementById('audio-preview');
      this.triggerOffset = 100;
      
      if (this.stickyBar) {
        this.init();
      }
    }

    init() {
      window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
      this.handleScroll();
    }

    handleScroll() {
      if (!this.triggerElement) {
        // Fallback: show after scrolling 500px
        const shouldShow = window.scrollY > 500;
        this.stickyBar.classList.toggle('is-visible', shouldShow);
        return;
      }

      const rect = this.triggerElement.getBoundingClientRect();
      const shouldShow = rect.top <= this.triggerOffset;
      this.stickyBar.classList.toggle('is-visible', shouldShow);
    }
  }

  // ============================================
  // FAQ ACCORDION
  // ============================================

  class FAQAccordion {
    constructor() {
      this.init();
    }

    init() {
      document.addEventListener('click', (e) => {
        const question = e.target.closest('.ah-faq-question');
        if (question) {
          this.toggle(question);
        }
      });
    }

    toggle(questionBtn) {
      const faqItem = questionBtn.closest('.ah-faq-item');
      if (!faqItem) return;

      const isOpen = faqItem.classList.contains('is-open');
      
      // Close all other items (optional - remove for multi-open)
      document.querySelectorAll('.ah-faq-item.is-open').forEach(item => {
        if (item !== faqItem) {
          item.classList.remove('is-open');
        }
      });

      faqItem.classList.toggle('is-open', !isOpen);
    }
  }

  // ============================================
  // LAZY LOADING WITH INTERSECTION OBSERVER
  // ============================================

  class LazyLoader {
    constructor() {
      this.init();
    }

    init() {
      const lazyElements = document.querySelectorAll('[data-lazy]');
      
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.load(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '100px'
        });

        lazyElements.forEach(el => observer.observe(el));
      } else {
        // Fallback for older browsers
        lazyElements.forEach(el => this.load(el));
      }
    }

    load(element) {
      // Load lazy images
      const lazySrc = element.dataset.lazySrc;
      if (lazySrc && element.tagName === 'IMG') {
        element.src = lazySrc;
      }

      // Remove skeleton/placeholder classes
      element.classList.remove('is-loading');
      element.classList.add('is-loaded');
      element.removeAttribute('data-lazy');
    }
  }

  // ============================================
  // MARQUEE ANIMATION CONTROL
  // ============================================

  class MarqueeController {
    constructor() {
      this.marquees = document.querySelectorAll('.ah-marquee');
      this.init();
    }

    init() {
      // Pause on hover (optional)
      this.marquees.forEach(marquee => {
        marquee.addEventListener('mouseenter', () => {
          marquee.style.animationPlayState = 'paused';
        });
        
        marquee.addEventListener('mouseleave', () => {
          marquee.style.animationPlayState = 'running';
        });
      });

      // Reduce motion for users who prefer it
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.marquees.forEach(marquee => {
          marquee.style.animation = 'none';
        });
      }
    }
  }

  // ============================================
  // SCROLL ANIMATIONS
  // ============================================

  class ScrollAnimations {
    constructor() {
      this.init();
    }

    init() {
      const animatedElements = document.querySelectorAll('[data-animate-on-scroll]');
      
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('ah-animate-fade-in');
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1
        });

        animatedElements.forEach(el => observer.observe(el));
      }
    }
  }

  // ============================================
  // WAVEFORM GENERATOR (Optional Enhancement)
  // ============================================

  function generateWaveformBars(container, data) {
    if (!container) return;

    // Ensure container is visible with flex layout - reduced gap for more bars
    container.style.setProperty('display', 'flex', 'important');
    container.style.setProperty('align-items', 'center', 'important');
    container.style.setProperty('justify-content', 'space-between', 'important');
    container.style.setProperty('gap', '1px', 'important');  // Reduced gap
    container.style.setProperty('height', '32px', 'important');
    container.style.setProperty('min-height', '32px', 'important');
    container.style.setProperty('box-sizing', 'border-box', 'important');
    container.style.setProperty('padding', '0 2px', 'important');
    container.style.setProperty('visibility', 'visible', 'important');
    container.style.setProperty('opacity', '1', 'important');

    // If no waveform data, just make existing Liquid bars visible
    if (!data) {
      container.querySelectorAll('.ah-waveform-bar').forEach((bar) => {
        bar.style.setProperty('opacity', '1', 'important');
        bar.style.setProperty('visibility', 'visible', 'important');
        bar.style.setProperty('display', 'block', 'important');
        bar.style.setProperty('background-color', 'rgba(201, 174, 127, 0.35)', 'important');
      });
      return;
    }

    // Interpolate data to double the bar count
    const interpolatedData = [];
    for (let i = 0; i < data.length; i++) {
      interpolatedData.push(data[i]);
      // Add interpolated value between current and next
      if (i < data.length - 1) {
        const avg = (data[i] + data[i + 1]) / 2;
        // Add slight variation for natural look
        interpolatedData.push(avg * (0.9 + Math.random() * 0.2));
      }
    }
    // Add one more at the end
    interpolatedData.push(data[data.length - 1] * 0.85);

    container.innerHTML = '';
    interpolatedData.forEach((height) => {
      const bar = document.createElement('div');
      bar.className = 'ah-waveform-bar';
      
      // Thinner bars (max 3px), more rounded, match Lovable style
      bar.style.cssText = `
        height: ${Math.max(height * 100, 12)}% !important;
        min-height: 4px !important;
        flex: 1 1 0 !important;
        min-width: 1px !important;
        max-width: 3px !important;
        border-radius: 1.5px !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: block !important;
        background-color: rgba(201, 174, 127, 0.35) !important;
        transition: background-color 0.1s ease !important;
      `;

      container.appendChild(bar);
    });
  }

  // ============================================
  // SCRUBBER SAFETY STYLES (prevents theme CSS from hiding it)
  // ============================================

  function initializeScrubbers() {
    // Also ensure the parent player container is visible
    document.querySelectorAll('.ah-player--master').forEach((player) => {
      player.style.setProperty('display', 'flex', 'important');
      player.style.setProperty('visibility', 'visible', 'important');
      player.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('[data-audio-scrubber]').forEach((scrubber) => {
      // Force visibility with !important to override any theme CSS
      scrubber.style.setProperty('display', 'block', 'important');
      scrubber.style.setProperty('position', 'relative', 'important');
      scrubber.style.setProperty('height', '6px', 'important');
      scrubber.style.setProperty('min-height', '6px', 'important');
      scrubber.style.setProperty('width', '100%', 'important');
      scrubber.style.setProperty('border-radius', '9999px', 'important');
      scrubber.style.setProperty('overflow', 'hidden', 'important');
      scrubber.style.setProperty('background-color', 'rgba(55, 62, 69, 0.8)', 'important');
      scrubber.style.setProperty('cursor', 'pointer', 'important');
      scrubber.style.setProperty('visibility', 'visible', 'important');
      scrubber.style.setProperty('opacity', '1', 'important');

      let fill = scrubber.querySelector('.ah-scrubber-fill');
      if (!fill) {
        fill = document.createElement('div');
        fill.className = 'ah-scrubber-fill';
        scrubber.appendChild(fill);
      }

      // Force fill visibility
      fill.style.setProperty('display', 'block', 'important');
      fill.style.setProperty('position', 'absolute', 'important');
      fill.style.setProperty('left', '0', 'important');
      fill.style.setProperty('top', '0', 'important');
      fill.style.setProperty('bottom', '0', 'important');
      fill.style.setProperty('height', '100%', 'important');
      // Don't set width here with !important - let updateProgress control it
      fill.style.setProperty('width', '0%');
      fill.style.setProperty('border-radius', '9999px', 'important');
      fill.style.setProperty('background-color', '#5C1D31', 'important');
      fill.style.setProperty('visibility', 'visible', 'important');
      fill.style.setProperty('opacity', '1', 'important');
    });

    console.info('[AfterHours] Scrubbers initialized:', document.querySelectorAll('[data-audio-scrubber]').length);
  }

  // Pre-defined waveform data (from original React implementation)
  const waveformData = {
    "wax-choir-page-four-em-74": [0.69,0.73,0.85,0.67,0.76,0.67,0.89,0.66,0.89,0.62,0.88,0.73,0.69,0.74,0.7,0.88,0.66,0.91,1,0.91,0.68,0.73,0.67,0.77,0.83,0.67,1,0.92,0.95,0.95,0.83,0.55,0.94,0.69,0.45],
    "wax-choir-one-more-time-em-74": [0.97,0.75,0.83,0.86,0.88,0.88,0.78,0.81,1,0.95,0.71,0.82,0.86,0.96,0.79,0.8,0.76,0.75,0.51,0.73,0.49,0.83,0.99,0.71,0.84,0.95,0.72,0.41,0.48,0.68,0.85,0.93,0.77,0.8,0.29],
    "wax-choir-lighten-up-abm-80": [0.68,0.73,0.97,0.85,0.98,0.85,0.96,0.87,0.88,0.74,0.91,0.81,0.86,0.87,0.75,0.97,0.68,0.93,0.63,0.92,0.89,0.93,0.91,0.85,0.96,0.83,0.8,0.78,0.98,0.74,0.94,0.77,0.96,1,0.52],
    "wax-choir-found-me-g-75": [0.37,0.27,0.31,0.31,0.37,0.29,0.22,0.39,0.33,0.33,0.28,0.77,0.81,0.51,0.28,0.62,0.69,0.72,0.3,0.22,0.37,0.29,0.35,0.25,0.36,0.28,0.37,0.29,0.69,1,0.78,0.31,0.46,0.6,0.17],
    "last-call-treacherous-ebm-82": [0.6,0.52,0.68,0.62,1,0.43,0.65,0.63,0.8,0.54,0.54,0.71,0.7,0.93,0.64,0.75,0.59,0.81,0.51,0.53,0.47,0.84,0.76,0.46,0.39,0.56,0.74,0.52,0.47,0.49,0.6,0.63,0.76,0.69,0.29],
  "last-call-song-for-you-c-m-77": [0.88,0.7,0.84,0.82,0.83,0.78,0.76,0.88,0.7,0.9,0.64,0.9,0.75,0.88,0.74,0.7,0.82,0.75,0.87,0.78,0.82,1,0.85,0.71,0.85,0.82,0.83,0.82,0.8,0.84,1,0.96,0.79,0.87,0.46],
  "last-call-hypnosis-f-m-72": [0.96,0.73,0.67,0.87,0.98,0.81,0.71,0.59,0.64,0.93,0.75,0.85,0.92,1,0.79,0.9,0.67,0.91,0.84,0.77,0.67,0.87,0.98,0.74,0.69,0.74,0.96,0.79,0.91,0.7,0.95,0.89,0.76,0.82,0.45],
  "last-call-fading-a-72": [0.72,0.36,0.7,0.39,0.69,0.42,0.33,0.34,0.73,0.54,0.48,0.65,0.35,0.8,0.4,0.34,0.35,0.71,0.78,0.94,1,0.46,0.66,0.44,0.32,0.32,0.78,0.76,0.2,0.28,0.62,0.56,0.37,0.35,0.19],
"velvet-room-the-same-smile-am-75": [0.52,0.47,0.72,0.9,0.67,0.44,1,0.62,0.91,0.5,0.59,0.75,0.93,0.58,0.61,0.95,0.57,0.58,0.45,0.61,0.71,0.65,0.53,0.82,0.81,0.84,0.78,0.48,0.72,0.72,0.91,0.48,0.92,0.73,0.3],
  "velvet-room-nobody-loves-abm-72": [0.7,0.55,0.69,0.51,0.59,0.66,0.68,0.78,0.78,0.66,0.59,0.61,0.48,0.67,0.63,0.7,0.77,0.73,0.32,0.48,1,0.87,0.4,0.32,0.82,0.95,0.64,0.31,0.64,0.92,0.85,0.33,0.44,0.95,0.49],
  "velvet-room-never-ever-dm-72": [0.85,0.67,0.58,0.93,0.93,0.83,0.54,0.65,0.75,0.83,0.96,0.96,0.99,0.81,0.86,0.92,1,0.89,0.62,0.67,0.77,0.86,1,0.63,0.67,0.7,0.79,0.84,0.8,0.94,0.73,0.87,0.71,0.98,0.41],
  "velvet-room-look-at-me-now-c-71": [0.6,0.6,0.71,0.79,0.8,0.49,0.55,0.93,0.77,0.33,0.9,0.96,0.74,0.78,0.59,0.63,0.86,0.86,0.99,0.65,0.65,1,0.91,0.67,0.66,0.98,0.98,0.92,0.65,0.69,0.95,0.64,0.49,0.19,0.37],
  "neon-nights-we-could-spend-am-90": [0.92,0.78,0.58,0.32,0.9,0.6,0.87,0.2,0.6,0.55,0.73,0.46,0.58,0.73,0.48,0.72,0.53,0.8,0.67,1,0.22,0.64,0.87,0.86,0.48,0.36,0.72,0.36,0.79,0.51,0.79,0.85,0.87,0.9,0.55],
  "neon-nights-want-to-feel-fm-77": [0.99,0.65,0.97,0.8,0.97,0.74,0.86,0.6,0.57,0.68,0.71,0.96,0.76,0.99,0.69,0.85,0.5,1,0.79,0.79,0.92,0.81,0.95,0.61,0.85,0.42,0.86,0.78,0.86,0.82,0.94,0.83,0.71,0.76,0.29],
  "neon-nights-a-ways-away-em-77": [0.79,0.71,0.61,0.93,0.98,0.87,0.67,0.64,0.84,0.75,0.65,0.62,1,0.96,0.85,0.64,0.69,0.76,0.63,0.62,0.59,0.73,0.54,0.78,0.85,0.92,0.78,0.61,0.64,0.65,0.68,0.59,0.81,0.83,0.54],
  "neon-nights-believe-in-me-d-83-50": [0.75,0.74,0.6,0.47,0.71,0.66,0.58,0.23,0.48,0.72,0.74,0.48,0.57,0.73,0.63,0.49,0.52,0.8,0.88,0.74,0.46,0.78,0.58,0.83,0.85,0.67,1,0.76,0.69,0.45,0.8,0.68,0.85,0.92,0.38],
  };

  // Initialize all waveforms with pre-defined data
  function initializeWaveforms() {
    const containers = Array.from(document.querySelectorAll('[data-audio-waveform]'));
    const missing = [];

    containers.forEach((container) => {
      const audioId = container.dataset.audioWaveform;
      const data = waveformData[audioId];

      // Always call generateWaveformBars - it handles both cases:
      // - With data: generates new bars from waveformData
      // - Without data: makes existing Liquid placeholder bars visible
      generateWaveformBars(container, data);

      if (!data) {
        missing.push(audioId);
      }
    });

    // Helpful debug (shows up in Shopify theme preview console)
    if (missing.length) {
      console.warn('[AfterHours] Missing waveformData keys for:', missing);
    }
    console.info('[AfterHours] Waveform containers found:', containers.length);
  }

  // ============================================
  // INITIALIZE ON DOM READY
  // ============================================

  function init() {
    new AudioPlayer();
    new StickyBar();
    new FAQAccordion();
    new LazyLoader();
    new MarqueeController();
    new ScrollAnimations();
    initializeScrubbers();
    initializeWaveforms();
  }

  // Guard against multiple initializations
  if (window.AfterHoursInitialized) return;
  window.AfterHoursInitialized = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for external use if needed
  window.AfterHours = {
    AudioPlayer,
    StickyBar,
    FAQAccordion,
    waveformData,
    generateWaveformBars
  };

})();
